import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Popover } from 'radix-ui';
import { useEffect, useId, useState, type ReactNode } from 'react';
import type { ScreenRecord } from '../extension-types.js';
import { COUNTRIES, CURRENCIES, displayText, formatMoney } from '../lib/format.js';
import { findEntity, optionsFor, useAppMeta, type AppEntity, type AppField } from '../lib/meta.js';
import { useReadOnlyView } from '../lib/read-only.js';
import { trpc } from '../lib/trpc.js';
import { Badge, cn, Input, NativeSelect, Textarea, type Tone } from './ui.js';

/** Reading and editing one field, by its type. Every screen uses these, so a new field type is added once. */

export function RecordLink({
  entity,
  id,
  children,
  className,
}: {
  entity: string;
  id: string;
  children: ReactNode;
  className?: string;
}) {
  if (useReadOnlyView()) return <span className={className}>{children}</span>;
  return (
    <Link
      to="/e/$entity/$id"
      params={{ entity, id }}
      className={cn('font-medium text-brand-strong hover:underline', className)}
    >
      {children}
    </Link>
  );
}

export function stateTone(entity: AppEntity, state: unknown): Tone {
  const category = typeof state === 'string' ? entity.states[state]?.category : undefined;
  if (category === 'done') return 'green';
  if (category === 'canceled') return 'neutral';
  if (state === 'partly_paid' || state === 'sent') return 'amber';
  return 'brand';
}

export function StateBadge({ entity, state }: { entity: AppEntity; state: unknown }) {
  if (typeof state !== 'string') return null;
  return <Badge tone={stateTone(entity, state)}>{entity.states[state]?.label ?? state}</Badge>;
}

const EMPTY = <span className="text-slate-400">—</span>;

/** Where a reference field points: its entity, or for polymorphic ones (`related_id`) the entity named beside it. */
export function referenceTarget(field: AppField, record: Record<string, unknown>): string | null {
  if (!field.reference) return null;
  if (field.reference !== '*') return field.reference;
  const named = record[field.key.replace(/_id$/, '_entity')];
  return typeof named === 'string' ? named : null;
}

export function FieldValue({
  entity,
  field,
  record,
  titles,
}: {
  entity: AppEntity;
  field: AppField;
  record: ScreenRecord;
  titles?: Record<string, string>;
}) {
  const meta = useAppMeta();
  const value = record[field.key];
  if (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  )
    return EMPTY;
  const known = titles ?? record.$titles ?? {};
  if (field.key === 'state' && Object.keys(entity.states).length > 0)
    return <StateBadge entity={entity} state={value} />;
  switch (field.type) {
    case 'reference': {
      const target = referenceTarget(field, record);
      const title = known[String(value)];
      if (!target || !findEntity(meta, target)) return <span>{title ?? 'Linked record'}</span>;
      return (
        <RecordLink entity={target} id={String(value)}>
          {title ?? 'Open'}
        </RecordLink>
      );
    }
    case 'email':
      return (
        <a href={`mailto:${String(value)}`} className="text-brand-strong hover:underline">
          {String(value)}
        </a>
      );
    case 'long_text':
      return <span className="whitespace-pre-wrap">{String(value)}</span>;
    case 'multi_select':
    case 'tags':
      return (
        <span className="flex flex-wrap gap-1">
          {displayText(meta, field, record, known)
            .split(', ')
            .map((label) => (
              <Badge key={label}>{label}</Badge>
            ))}
        </span>
      );
    case 'money':
    case 'integer':
    case 'decimal':
    case 'percent':
      return <span className="tabular-nums">{displayText(meta, field, record, known)}</span>;
    default:
      return <span>{displayText(meta, field, record, known)}</span>;
  }
}

export interface FieldInputProps {
  field: AppField;
  value: unknown;
  onChange: (value: unknown) => void;
  /** The record being edited, for options that depend on another field. */
  record: Record<string, unknown>;
  id?: string;
  invalid?: boolean;
  disabled?: boolean;
}

export function FieldInput({
  field,
  value,
  onChange,
  record,
  id,
  invalid,
  disabled,
}: FieldInputProps) {
  const meta = useAppMeta();
  const common = { id, 'aria-invalid': invalid || undefined, disabled };
  const text = value === null || value === undefined ? '' : String(value);
  switch (field.type) {
    case 'long_text':
      return <Textarea {...common} value={text} onChange={(e) => onChange(e.target.value)} />;
    case 'integer':
      return (
        <Input
          {...common}
          type="number"
          step="1"
          value={text}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
    case 'decimal':
    case 'money':
    case 'percent':
      return (
        <div className="relative">
          <Input
            {...common}
            inputMode="decimal"
            value={text}
            onChange={(e) => onChange(e.target.value)}
            className={field.type === 'percent' ? 'pr-8' : ''}
          />
          {field.type === 'percent' && (
            <span className="pointer-events-none absolute top-2 right-3 text-sm text-slate-400">
              %
            </span>
          )}
        </div>
      );
    case 'date':
      return (
        <Input
          {...common}
          type="date"
          value={text.slice(0, 10)}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
    case 'datetime': {
      const local = text ? toLocalInput(text) : '';
      return (
        <Input
          {...common}
          type="datetime-local"
          value={local}
          onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)}
        />
      );
    }
    case 'boolean':
      return (
        <input
          {...common}
          type="checkbox"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
          className="size-4 rounded border-slate-300 accent-[var(--db-primary)]"
        />
      );
    case 'select': {
      const options = optionsFor(meta, field, record);
      return (
        <NativeSelect {...common} value={text} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">{field.required ? 'Choose…' : 'None'}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </NativeSelect>
      );
    }
    case 'multi_select': {
      const options = optionsFor(meta, field, record);
      const chosen = new Set(Array.isArray(value) ? (value as string[]) : []);
      return (
        <div
          id={id}
          className="flex flex-wrap gap-x-4 gap-y-1.5 rounded-md border border-slate-200 bg-surface px-3 py-2"
        >
          {options.map((o) => (
            <label key={o.value} className="flex items-center gap-1.5 text-sm text-slate-700">
              <input
                type="checkbox"
                disabled={disabled}
                checked={chosen.has(o.value)}
                className="size-4 accent-[var(--db-primary)]"
                onChange={(e) => {
                  const next = new Set(chosen);
                  if (e.target.checked) next.add(o.value);
                  else next.delete(o.value);
                  onChange(options.map((x) => x.value).filter((v) => next.has(v)));
                }}
              />
              {o.label}
            </label>
          ))}
        </div>
      );
    }
    case 'tags':
      return (
        <Input
          {...common}
          placeholder="Comma-separated"
          value={Array.isArray(value) ? value.join(', ') : text}
          onChange={(e) =>
            onChange(
              e.target.value
                .split(',')
                .map((t) => t.trim())
                .filter(Boolean),
            )
          }
        />
      );
    case 'reference': {
      const target = referenceTarget(field, record);
      if (!target)
        return (
          <Input {...common} value={text} onChange={(e) => onChange(e.target.value || null)} />
        );
      return (
        <ReferencePicker
          entity={target}
          value={typeof value === 'string' ? value : null}
          onChange={onChange}
          required={Boolean(field.required)}
          {...(id !== undefined && { id })}
          {...(invalid !== undefined && { invalid })}
          {...(disabled !== undefined && { disabled })}
        />
      );
    }
    case 'currency':
      return (
        <NativeSelect {...common} value={text} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">Choose…</option>
          {CURRENCIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </NativeSelect>
      );
    case 'country':
      return (
        <NativeSelect {...common} value={text} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">Choose…</option>
          {COUNTRIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </NativeSelect>
      );
    case 'email':
      return (
        <Input {...common} type="email" value={text} onChange={(e) => onChange(e.target.value)} />
      );
    default:
      return (
        <Input
          {...common}
          value={text}
          maxLength={field.max_length}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}

function toLocalInput(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function useDebounced<T>(value: T, ms: number): T {
  const [current, setCurrent] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setCurrent(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return current;
}

/** Choose a record by its title. Searches as you type, with the reader's permissions. */
export function ReferencePicker({
  entity,
  value,
  onChange,
  id,
  invalid,
  disabled,
  required,
}: {
  entity: string;
  value: string | null;
  onChange: (value: string | null) => void;
  id?: string;
  invalid?: boolean;
  disabled?: boolean;
  required?: boolean;
}) {
  const meta = useAppMeta();
  const target = findEntity(meta, entity);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search, 200);
  const listId = useId();
  const selected = useQuery({
    ...trpc.records.lookup.queryOptions({ entity, ids: value ? [value] : [] }),
    enabled: Boolean(value),
  });
  const results = useQuery({
    ...trpc.records.lookup.queryOptions({
      entity,
      ...(debounced && { search: debounced }),
      limit: 20,
    }),
    enabled: open,
  });
  const title = value
    ? (selected.data?.[0]?.title ?? (selected.isLoading ? 'Loading…' : 'Unknown record'))
    : '';
  const choose = (next: string | null) => {
    onChange(next);
    setOpen(false);
    setSearch('');
  };
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        id={id}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-slate-300 bg-surface px-3 text-left text-sm shadow-sm focus:border-brand focus:ring-2 focus:ring-brand-muted focus:outline-none disabled:bg-slate-100 aria-[invalid=true]:border-red-500"
      >
        <span className={cn('truncate', !value && 'text-slate-400')}>
          {value ? title : `Choose ${target?.label.toLowerCase() ?? 'a record'}…`}
        </span>
        <ChevronsUpDown className="size-4 shrink-0 text-slate-400" aria-hidden />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="z-50 w-[var(--radix-popover-trigger-width)] min-w-64 rounded-md border border-slate-200 bg-surface p-1 shadow-lg"
        >
          <Input
            autoFocus
            placeholder={`Search ${target?.plural.toLowerCase() ?? 'records'}`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-1"
          />
          <ul id={listId} role="listbox" className="max-h-64 overflow-y-auto">
            {results.isLoading && (
              <li className="px-2 py-1.5 text-sm text-slate-500">Searching…</li>
            )}
            {results.data?.length === 0 && (
              <li className="px-2 py-1.5 text-sm text-slate-500">Nothing found</li>
            )}
            {results.data?.map((r) => (
              <li key={r.id} role="option" aria-selected={r.id === value}>
                <button
                  type="button"
                  onClick={() => choose(r.id)}
                  className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-slate-100"
                >
                  <span className="truncate">{r.title || 'Untitled'}</span>
                  {r.id === value && <Check className="size-4 text-brand" aria-hidden />}
                </button>
              </li>
            ))}
          </ul>
          {value && !required && (
            <button
              type="button"
              onClick={() => choose(null)}
              className="mt-1 flex w-full items-center gap-1.5 border-t border-slate-100 px-2 pt-1.5 pb-1 text-left text-sm text-slate-500 hover:text-slate-800"
            >
              <X className="size-3.5" aria-hidden /> Clear
            </button>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** A money amount in the record's currency, for totals. */
export function Money({ value, currency }: { value: unknown; currency: string }) {
  return <span className="tabular-nums">{formatMoney(value, currency)}</span>;
}
